import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/hooks/use-language';
import { usePageHero } from '@/hooks/usePageHero';
import { MapPin, Phone, Search, Navigation, Info, ExternalLink, AlertTriangle, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LocalizedText } from '@/lib/products';
import { useLocation } from 'react-router-dom';
import { getCachedWhereToBuy, fetchWhereToBuy } from '@/lib/globalDataCache';
import { SEO } from '@/components/ui/SEO';
import { buildBreadcrumbs } from '@/lib/schema';
import { getPagePath, type Lang } from '@/lib/localizedRoutes';
import { trackClick } from '@/lib/analytics';
import { MobileDealerCard, type DealerCardData } from '@/components/layout/MobileDealerCard';

interface LocalizedStr {
  ru: string;
  en: string;
  uz: string;
}

interface Dealer {
  id: string; // use string id matching DB
  name: string;
  type: string;
  city: LocalizedStr;
  country: LocalizedStr;
  region: LocalizedStr;
  address: LocalizedStr;
  phone: string;
  coordinates: [number, number];
  rating?: number;
  hours?: string;
  website?: string;
  // Extended fields — localized
  subtitle?: LocalizedText;
  description?: LocalizedText;
  photos?: string[];
  products?: { id: string; image: string; name: LocalizedText }[];
  instagramUrl?: string;
  youtubeUrl?: string;
  tiktokUrl?: string;
  facebookUrl?: string;
}

// Yandex Maps API key. Tried in order: build-time env, runtime window global,
// hardcoded fallback. The hardcoded value is a domain-locked public web key,
// not a secret credential. All comments here intentionally use ASCII only and
// avoid the literal closing-script-tag substring so the file is safe to
// transfer through any HTML-aware editor.
const YANDEX_API_KEY_FALLBACK = '443d876f-3466-4b4c-9561-ed45c41d79f3';
function readYandexApiKey(): string {
  const fromEnv = import.meta.env.VITE_YANDEX_MAPS_API_KEY as string | undefined;
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined') {
    const fromWindow = (window as unknown as { __YANDEX_MAPS_API_KEY?: string }).__YANDEX_MAPS_API_KEY;
    if (fromWindow) return fromWindow;
  }
  return YANDEX_API_KEY_FALLBACK;
}
const YANDEX_API_KEY = readYandexApiKey();

const cleanPhone = (phone: string) => phone.replace(/[^\d+]/g, '');

// Type label mapping including 'store'
const TYPE_LABEL_MAP: Record<string, Record<string, string>> = {
  dealer: { ru: 'Дилер', en: 'Dealer', uz: 'Diler' },
  retail: { ru: 'Розница', en: 'Retail', uz: 'Chakana' },
  showroom: { ru: 'Шоурум', en: 'Showroom', uz: 'Showroom' },
  warehouse: { ru: 'Склад', en: 'Warehouse', uz: 'Ombor' },
  store: { ru: 'Магазин', en: 'Store', uz: 'Do\'kon' },
};

function getTypeLabel(type: string, language: string): string {
  return TYPE_LABEL_MAP[type]?.[language] ?? TYPE_LABEL_MAP[type]?.['ru'] ?? type;
}

export const WhereToBuy: React.FC<{ seoEnabled?: boolean }> = ({ seoEnabled = true }) => {
  const { t, language } = useLanguage();
  const whereToBuyHero = usePageHero({
    pageKey: 'wheretobuy',
    lang: language,
    fallbacks: {
      eyebrow: t('page.whereToBuy.points'),
      title: t('page.whereToBuy.title'),
    },
  });
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1024px)');
    const onChange = () => {
      const matches = mql.matches;
      setIsMobile(matches);
      if (!matches) setIsMapEnabled(true);
    };
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const rootRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (isMobile && rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      setHeaderHeight(rect.top);
    }
  }, [isMobile]);

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedDealerId, setSelectedDealerId] = useState<string | null>(null);
  const [mobileDealerCard, setMobileDealerCard] = useState<DealerCardData | null>(null);
  const [isMapEnabled, setIsMapEnabled] = useState(false);
  const [isPendingNavigation, setIsPendingNavigation] = useState(false);

  // Map state - used only to programmatically fly to a point
  const [mapCenter, setMapCenter] = useState<[number, number]>([41.2995, 69.2401]);
  const [zoom, setZoom] = useState(5);

  const [mapStatus, setMapStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [ymapsInstance, setYmapsInstance] = useState<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const dealerListRef = useRef<HTMLDivElement>(null);
  // Store placemark refs by dealer id (string)
  const placemarksRef = useRef<Record<string, any>>({});
  const layoutsRef = useRef<any>({});
  const mapToggleGuardRef = useRef(false);
  // Prevent boundschange feedback loop
  const suppressBoundsRef = useRef(false);

  const headerVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i: number) => ({
      opacity: 1, x: 0,
      transition: { delay: i * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }
    })
  };

  const toLoc = (val: any): LocalizedStr => {
    if (!val) return { ru: '', en: '', uz: '' };
    if (typeof val === 'object') return { ru: val.ru ?? '', en: val.en ?? '', uz: val.uz ?? '' };
    return { ru: String(val), en: String(val), uz: String(val) };
  };

  const mapPoints = (dbPoints: any[] | null): Dealer[] => {
    if (!dbPoints) return [];
    return dbPoints.map((p: any) => ({
      id: p.id, name: p.name, type: p.type,
      city: toLoc(p.city), country: toLoc(p.country),
      region: toLoc(p.region), address: toLoc(p.address),
      phone: p.phone, website: p.website,
      rating: p.rating, hours: p.hours, coordinates: p.coordinates,
      subtitle: p.subtitle, description: p.description, photos: p.photos,
      products: p.products, instagramUrl: p.instagramUrl,
      youtubeUrl: p.youtubeUrl, tiktokUrl: p.tiktokUrl, facebookUrl: p.facebookUrl,
    }));
  };

  const [dealers, setDealers] = useState<Dealer[]>(() => {
    const cached = getCachedWhereToBuy();
    return cached ? mapPoints(cached) : [];
  });
  const [dealersLoaded, setDealersLoaded] = useState(() => getCachedWhereToBuy() !== null);

  useEffect(() => {
    const cached = getCachedWhereToBuy();
    if (cached) { setDealers(mapPoints(cached)); setDealersLoaded(true); }

    fetchWhereToBuy().then((dbPoints) => {
      setDealers(mapPoints(dbPoints));
      setDealersLoaded(true);
    }).catch(() => setDealersLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredDealers = dealers.filter(d => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const lang = language as 'ru' | 'en' | 'uz';
    return [
      d.city[lang], d.city.ru, d.country[lang], d.country.ru,
      d.region[lang], d.region.ru, d.address[lang], d.address.ru,
    ].join(' ').toLowerCase().includes(query);
  });

  // Scroll list to a specific dealer card
  const scrollToCard = useCallback((dealerId: string) => {
    if (!dealerListRef.current) return;
    const card = dealerListRef.current.querySelector(`[data-dealer-id="${dealerId}"]`) as HTMLElement | null;
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  // Fly map to dealer with optional y-offset
  const flyToDealer = useCallback((dealer: Dealer) => {
    const map = (mapContainerRef.current as any)?.__ymapsMap;
    if (map && ymapsInstance) {
      const projection = map.options.get('projection');
      const zoomLevel = 15;
      const pixelCenter = projection.toGlobalPixels(dealer.coordinates, zoomLevel);
      const mapHeight = map.container.getElement().offsetHeight;
      const offsetY = mapHeight * 0.20;
      const offsetPixelCenter = [pixelCenter[0], pixelCenter[1] + offsetY];
      const offsetGeoCenter = projection.fromGlobalPixels(offsetPixelCenter, zoomLevel);
      suppressBoundsRef.current = true;
      map.setCenter(offsetGeoCenter, zoomLevel, { duration: 300 });
      setTimeout(() => { suppressBoundsRef.current = false; }, 400);
    } else {
      setMapCenter(dealer.coordinates);
      setZoom(15);
    }
  }, [ymapsInstance]);

  const openMobileCard = useCallback((dealer: Dealer) => {
    const cardData: DealerCardData = {
      id: dealer.id,
      name: dealer.name,
      type: dealer.type,
      address: dealer.address,
      phone: dealer.phone,
      website: dealer.website,
      hours: dealer.hours,
      rating: dealer.rating,
      coordinates: dealer.coordinates,
      subtitle: dealer.subtitle,
      description: dealer.description,
      photos: dealer.photos,
      products: dealer.products,
      instagramUrl: dealer.instagramUrl,
      youtubeUrl: dealer.youtubeUrl,
      tiktokUrl: dealer.tiktokUrl,
      facebookUrl: dealer.facebookUrl,
    };
    setMobileDealerCard(cardData);
  }, []);

  // FIX 3: Chip click = navigate only, NO card open
  const handleChipClick = useCallback((dealer: Dealer) => {
    setSelectedDealerId(dealer.id);
    flyToDealer(dealer);
    // Do NOT open the fullscreen card — chips are navigation only
  }, [flyToDealer]);

  const handleDealerClick = useCallback((dealer: Dealer, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (selectedDealerId === dealer.id) {
      setSelectedDealerId(null);
      if (isMobile) setMobileDealerCard(null);
    } else {
      setSelectedDealerId(dealer.id);
      flyToDealer(dealer);
      if (!isMobile) {
        // desktop: open normally
      }
    }
  }, [selectedDealerId, flyToDealer, isMobile]);

  // FIX 3: Marker click = navigate + open fullscreen card on mobile
  const handleMarkerClick = useCallback((dealer: Dealer) => {
    setSelectedDealerId(dealer.id);
    flyToDealer(dealer);
    if (!isMobile) {
      setTimeout(() => scrollToCard(dealer.id), 50);
    } else {
      setIsMapEnabled(true);
      openMobileCard(dealer);
    }
  }, [flyToDealer, isMobile, scrollToCard, openMobileCard]);

  // Load Yandex Maps script
  useEffect(() => {
    if (!YANDEX_API_KEY) { setMapStatus('error'); return; }
    if ((window as any).ymaps) {
      (window as any).ymaps.ready(() => {
        setYmapsInstance((window as any).ymaps);
        setMapStatus('ready');
      });
      return;
    }
    const existing = document.querySelector('script[src*="api-maps.yandex.ru"]');
    if (existing) {
      existing.addEventListener('load', () => {
        (window as any).ymaps?.ready(() => {
          setYmapsInstance((window as any).ymaps);
          setMapStatus('ready');
        });
      });
      existing.addEventListener('error', () => setMapStatus('error'));
      return;
    }
    const langCode = language === 'ru' ? 'ru_RU' : language === 'uz' ? 'uz_UZ' : 'en_US';
    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${YANDEX_API_KEY}&lang=${langCode}`;
    script.async = true;
    script.onload = () => {
      (window as any).ymaps?.ready(() => {
        setYmapsInstance((window as any).ymaps);
        setMapStatus('ready');
      });
    };
    script.onerror = () => setMapStatus('error');
    document.head.appendChild(script);
  }, [language]);

  // Initialize map
  useEffect(() => {
    if (mapStatus !== 'ready' || !ymapsInstance || !mapContainerRef.current) return;
    if ((mapContainerRef.current as any).__ymapsMap) return;

    const map = new ymapsInstance.Map(mapContainerRef.current, {
      center: mapCenter,
      zoom: zoom,
      controls: ['zoomControl', 'fullscreenControl'],
    }, {
      autoFitToViewport: 'always',
      suppressMapOpenBlock: true,
      yandexMapDisablePoiInteractivity: true,
    });

    // Define custom marker layouts
    const HollowLayout = ymapsInstance.templateLayoutFactory.createClass(
      '<div class="custom-marker"><div class="marker-hollow"></div></div>'
    );
    const ActiveLayout = ymapsInstance.templateLayoutFactory.createClass(
      '<div class="custom-marker marker-active-wrap">' +
      '<div class="marker-glow"></div>' +
      '<div class="marker-active-dot"></div>' +
      '</div>'
    );

    layoutsRef.current = { hollow: HollowLayout, active: ActiveLayout };
    (mapContainerRef.current as any).__ymapsMap = map;

    // boundschange: only sync React state when NOT suppressed
    // This avoids the feedback loop: React state change → map setCenter → boundschange → state change → ...
    map.events.add('boundschange', (e: any) => {
      if (suppressBoundsRef.current) return;
      const newCenter = e.get('newCenter');
      const newZoom = e.get('newZoom');
      setMapCenter(newCenter);
      setZoom(newZoom);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStatus, ymapsInstance]);

  // Update placemarks when dealers or selection changes
  // KEY FIX: do NOT debounce this — update synchronously on each render
  useEffect(() => {
    if (mapStatus !== 'ready' || !ymapsInstance || !mapContainerRef.current || !layoutsRef.current.hollow) return;
    const map = (mapContainerRef.current as any).__ymapsMap;
    if (!map) return;

    // Remove all existing geo objects
    map.geoObjects.removeAll();
    placemarksRef.current = {};

    // Re-add all dealers as placemarks with correct layout
    dealers.forEach(dealer => {
      const isSelected = selectedDealerId === dealer.id;
      const placemark = new ymapsInstance.Placemark(
        dealer.coordinates,
        {},
        {
          // Use the correct layout immediately based on current selection — no delay
          iconLayout: isSelected ? layoutsRef.current.active : layoutsRef.current.hollow,
          iconOffset: [0, 0],
          iconShape: { type: 'Circle', coordinates: [0, -20], radius: 20 },
          hasBalloon: false,
          hasHint: false,
          openBalloonOnClick: false,
          interactive: true,
        }
      );

      placemarksRef.current[dealer.id] = placemark;

      // Attach click: dealer object is captured by closure — no stale ref issue
      placemark.events.add('click', (e: any) => {
        e.preventDefault();
        handleMarkerClick(dealer);
      });

      map.geoObjects.add(placemark);
    });
    // Depend on dealers, selectedDealerId, and ymapsInstance — NOT on handleMarkerClick (stable via useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealers, selectedDealerId, mapStatus, ymapsInstance]);

  // Programmatic map center/zoom sync (only when we explicitly set mapCenter/zoom, e.g. dealer click)
  // This effect runs only when mapCenter/zoom change and the map's current state differs.
  useEffect(() => {
    if (!mapContainerRef.current || !(mapContainerRef.current as any).__ymapsMap) return;
    const map = (mapContainerRef.current as any).__ymapsMap;
    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();
    const centerDiff = Math.abs(currentCenter[0] - mapCenter[0]) + Math.abs(currentCenter[1] - mapCenter[1]);
    const zoomDiff = Math.abs(currentZoom - zoom);
    if (centerDiff > 0.00001 || zoomDiff > 0.1) {
      suppressBoundsRef.current = true;
      map.setCenter(mapCenter, zoom, { duration: 300, checkZoomRange: true });
      setTimeout(() => { suppressBoundsRef.current = false; }, 400);
    }
  }, [mapCenter, zoom]);

  // Auto-fit bounds when filtered dealers change (no dealer selected)
  useEffect(() => {
    if (!mapContainerRef.current || !(mapContainerRef.current as any).__ymapsMap || !ymapsInstance) return;
    // FIX 2: don't auto-pan while search is open — prevents map jump when keyboard appears
    if (isSearchOpen) return;
    if (filteredDealers.length === 0 || selectedDealerId) return;
    const map = (mapContainerRef.current as any).__ymapsMap;
    const points = (searchQuery ? filteredDealers : dealers).map(d => d.coordinates);
    if (points.length === 0) return;
    const bounds = ymapsInstance.util.bounds.fromPoints(points);
    suppressBoundsRef.current = true;
    map.setBounds(bounds, { checkZoomRange: true, duration: 0, zoomMargin: 50 }).then(() => {
      if (filteredDealers.length === 1) map.setZoom(12, { duration: 0 });
      suppressBoundsRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDealers.length, searchQuery, ymapsInstance, isSearchOpen]);

  // Navigation guard for mobile map
  useEffect(() => {
    const handleNavigationClick = (e: MouseEvent) => {
      if (!isMapEnabled) return;
      if (mapToggleGuardRef.current) return;
      const target = e.target as HTMLElement;
      const wtbRoot = document.getElementById('where-to-buy-root');
      if (wtbRoot && wtbRoot.contains(target)) return;
      const interactiveEl = target.closest('a') || target.closest('button') || target.closest('[role="button"]') || target.closest('[data-index]');
      if (!interactiveEl) return;
      const href = interactiveEl.getAttribute('href') || interactiveEl.getAttribute('to');
      const isTab = interactiveEl.hasAttribute('data-index');
      if (!href && !isTab) return;
      if (href === '/where-to-buy') return;
      setIsPendingNavigation(true);
    };
    document.addEventListener('click', handleNavigationClick, { capture: true });
    return () => document.removeEventListener('click', handleNavigationClick, { capture: true });
  }, [isMapEnabled]);

  useEffect(() => {
    const isAtWtb = location.pathname === '/where-to-buy';
    if (mapToggleGuardRef.current) return;
    if (!isAtWtb && isMapEnabled) {
      const timer = setTimeout(() => {
        if (mapToggleGuardRef.current) return;
        requestAnimationFrame(() => {
          if (mapToggleGuardRef.current) return;
          if (window.location.pathname !== '/where-to-buy') {
            setIsMapEnabled(false);
            setIsPendingNavigation(false);
          } else {
            setIsPendingNavigation(false);
          }
        });
      }, 500);
      return () => clearTimeout(timer);
    } else if (isAtWtb) {
      setIsPendingNavigation(false);
    }
  }, [location.pathname, isMapEnabled]);

  // Block horizontal swipe on mobile when map is active
  useEffect(() => {
    if (isMobile && isMapEnabled) {
      const track = document.querySelector('.mobile-swipe-track') as HTMLElement;
      if (track) {
        const currentScrollLeft = track.scrollLeft;
        track.style.overflowX = 'hidden';
        track.style.scrollSnapType = 'none';
        track.scrollLeft = currentScrollLeft;
      }
      return () => {
        const t = document.querySelector('.mobile-swipe-track') as HTMLElement;
        if (t) {
          const sl = t.scrollLeft;
          t.style.overflowX = 'auto';
          t.style.scrollSnapType = 'x mandatory';
          t.scrollLeft = sl;
        }
      };
    }
  }, [isMobile, isMapEnabled]);

  return (
    <>
      <SEO
        enabled={seoEnabled}
        title={language === 'uz' ? 'SARGO qayerdan sotib olish | Rasmiy dilerlar va savdo nuqtalari' : language === 'en' ? 'Where to Buy SARGO | Official Dealers and Sales Points' : 'Где купить SARGO | Официальные дилеры и точки продаж'}
        description={language === 'uz'
          ? "SARGO mahsulotlari uchun rasmiy dilerlarni va savdo nuqtalarini xaritada toping. Joriy kontaktlar, manzillar va SARGO mahsulotlarini sotib olish ma'lumotlari."
          : language === 'en'
            ? 'Find official SARGO dealers and sales points on the map. Current contacts, addresses and information on purchasing SARGO products.'
            : 'Найдите официальных дилеров и точки продаж SARGO на карте. Актуальные контакты, адреса и информация о покупке продукции SARGO.'}
        canonical={getPagePath('where-to-buy', language as Lang)}
        schema={buildBreadcrumbs(window.location.origin, [
          { name: language === 'uz' ? 'Bosh sahifa' : language === 'en' ? 'Home' : 'Главная', url: `/${language}` },
          { name: language === 'uz' ? 'Qayerdan sotib olish' : language === 'en' ? 'Where to Buy' : 'Где купить', url: getPagePath('where-to-buy', language as Lang) },
        ])} />
      <div
        id="where-to-buy-root"
        ref={rootRef}
        className={cn(
          'pb-16 lg:pb-24',
          isMobile && 'pb-0',
          isMobile && selectedDealerId && 'map-dealer-active'
        )}
        style={{
          WebkitTextSizeAdjust: '100%',
          ...(isMobile ? { height: '100dvh', overflow: 'hidden', position: 'relative' } : {})
        }}
      >
        {isMobile && (
          <style>{`
          .mobile-swipe-page:has(#where-to-buy-root) footer { display: none !important; }
          .mobile-swipe-page:has(#where-to-buy-root) { overflow: hidden !important; }
          .mobile-swipe-page:has(#where-to-buy-root) > div { height: 100% !important; }
        `}</style>
        )}

        {/* Header — desktop only */}
        {!isMobile && (
          <section className="pt-24 md:pt-32 pb-12 lg:pb-16 relative">
            <div className="grid-cells opacity-50" />
            <div className="container-custom relative z-10">
              <div className="space-y-4 lg:space-y-6">
                {whereToBuyHero.eyebrow?.trim() && (
                  <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={headerVariants} className="flex items-center gap-2 lg:gap-3">
                    <div className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-primary shadow-[0_0_10px_rgba(176,138,90,1)]"></div>
                    <p className="text-primary font-black uppercase tracking-[0.4em] text-[10px]">{whereToBuyHero.eyebrow}</p>
                  </motion.div>
                )}
                {whereToBuyHero.title?.trim() && (
                  <motion.h1 custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={headerVariants} className="font-black uppercase tracking-tighter leading-[0.95] lg:leading-[0.8]">
                    {whereToBuyHero.title}<span className="text-primary">.</span>
                  </motion.h1>
                )}
                {whereToBuyHero.subtitle?.trim() && (
                  <motion.p custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={headerVariants} className="text-white/40 max-w-xl text-sm md:text-base lg:text-lg leading-relaxed font-black uppercase tracking-wider">
                    {whereToBuyHero.subtitle}
                  </motion.p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Main Content */}
        {isMobile ? (
          <div className="relative overflow-hidden w-full" style={{ height: `calc(100dvh - ${headerHeight}px)` }}>
            {/* Full Screen Map */}
            <div className="absolute inset-0 overflow-hidden bg-background">
              <div
                ref={mapContainerRef}
                className={cn(
                  'absolute inset-0 w-full h-full transition-all duration-700',
                  !isMapEnabled && 'grayscale-[0.8] brightness-[0.7] saturate-[0.5] opacity-50',
                  mapStatus !== 'ready' && 'opacity-0'
                )}
                style={{ pointerEvents: isMapEnabled ? 'auto' : 'none' }}
              />
              {!isMapEnabled && <div className="absolute inset-0 z-20 bg-transparent" style={{ touchAction: 'pan-x' }} />}

              {/* FIX 1: Toggle Button — hidden when fullscreen card is open */}
              <div
                className="absolute right-4 z-[9999] transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)] will-change-transform"
                style={{
                  bottom: 40,
                  // Hide completely when dealer card is open so it never overlaps
                  opacity: mobileDealerCard ? 0 : 1,
                  pointerEvents: mobileDealerCard ? 'none' : 'auto',
                }}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    mapToggleGuardRef.current = true;
                    setIsMapEnabled(prev => !prev);
                    if (isMapEnabled) setSelectedDealerId(null);
                    setTimeout(() => { mapToggleGuardRef.current = false; }, 400);
                  }}
                  className={cn(
                    'w-12 h-12 rounded-full flex flex-col items-center justify-center transition-all duration-500 shadow-2xl',
                    isMapEnabled
                      ? 'bg-primary border-2 border-primary text-white scale-110 shadow-[0_0_20px_rgba(176,138,90,0.4)]'
                      : 'bg-[#13100B]/90 backdrop-blur-lg border border-[#B08A5A]/50 text-white/70 shadow-[0_0_12px_rgba(176,138,90,0.25)]'
                  )}
                >
                  <span className="text-[7px] font-black uppercase tracking-tighter leading-none mb-0.5">Карта</span>
                  <span className="text-[8px] font-black uppercase tracking-widest">{isMapEnabled ? 'Вкл' : 'Выкл'}</span>
                </button>
              </div>

              {mapStatus === 'loading' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 z-30">
                  <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
                </div>
              )}

              <AnimatePresence>
                {isMapEnabled && (
                  <>
                    {/* Dealer Chips Panel */}
                    <motion.div
                      key="dealer-chips-panel"
                      initial={{ y: '-110%', opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: '-110%', opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute top-0 left-0 right-0 z-50 bg-[#13100B] border-b border-white/10 flex items-center"
                      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', minHeight: '56px' }}
                    >
                      <div className="flex items-center gap-2 px-4 overflow-x-auto no-scrollbar scroll-smooth w-full">
                        {filteredDealers.map((dealer) => (
                          <button
                            key={dealer.id}
                            onClick={() => handleChipClick(dealer)}
                            className={cn(
                              'flex items-center gap-2 py-2 px-4 rounded-full border border-white/5 whitespace-nowrap transition-all duration-300 min-w-fit max-w-[180px]',
                              selectedDealerId === dealer.id
                                ? 'bg-primary/20 border-primary/50 text-primary shadow-[0_0_15px_rgba(176,138,90,0.2)]'
                                : 'bg-background/80 border-white/5 text-white/50'
                            )}
                          >
                            <MapPin size={14} className={cn('shrink-0 transition-colors', selectedDealerId === dealer.id ? 'text-primary' : 'text-white/20')} />
                            <span className="text-[10px] font-black uppercase tracking-widest leading-none truncate">{dealer.name}</span>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            {/* ── Fullscreen Mobile Dealer Card ── */}
            <MobileDealerCard
              dealer={mobileDealerCard}
              onClose={() => {
                setMobileDealerCard(null);
                setSelectedDealerId(null);
              }}
            />
          </div>
        ) : (
          /* ── DESKTOP ── */
          <section id="map-section" className="py-12 lg:py-20 relative">
            <div className="grid-cells-accent opacity-10" />
            <div className="container-custom relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                {/* Left Column: Search + Dealers List */}
                <div className="lg:col-span-1 flex flex-col space-y-6 order-2 lg:order-1">
                  {/* Search Bar */}
                  <div className={cn(
                    'relative top-[15px] flex items-center transition-all duration-300 h-12',
                    isSearchOpen ? 'w-full' : 'w-12',
                  )}>
                    <button
                      onClick={() => setIsSearchOpen(!isSearchOpen)}
                      className={cn(
                        'absolute left-0 z-10 w-12 h-12 flex items-center justify-center transition-colors border',
                        isSearchOpen ? 'text-primary border-primary/20 bg-background/60' : 'text-white/40 bg-background/40 border-white/5 hover:border-primary/40'
                      )}
                    >
                      <Search size={18} />
                    </button>
                    <AnimatePresence>
                      {isSearchOpen && (
                        <motion.div
                          initial={{ width: 48, opacity: 0 }}
                          animate={{ width: '100%', opacity: 1 }}
                          exit={{ width: 48, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeOut' }}
                          className="overflow-hidden h-12"
                        >
                          <input
                            autoFocus
                            type="text"
                            placeholder={t('wtb.search_placeholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-[#13100B]/95 backdrop-blur-xl border border-white/10 py-3 pl-14 pr-10 text-[10px] xl:text-[11px] placeholder:text-[10px] xl:placeholder:text-[11px] font-black uppercase tracking-[0.2em] text-white focus:outline-none focus:border-primary transition-all min-h-[48px]"
                          />
                          {searchQuery && (
                            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30">
                              <X size={14} />
                            </button>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Dealers List — smooth native scroll, no custom wheel handler */}
                  <div
                    ref={dealerListRef}
                    className="space-y-3 lg:space-y-4 lg:h-[520px] overflow-y-auto pr-0 lg:pr-2"
                    style={{
                      scrollBehavior: 'smooth',
                      WebkitOverflowScrolling: 'touch',
                      overscrollBehavior: 'contain',
                      // Hide scrollbar but keep it functional
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgba(176,138,90,0.2) transparent',
                    }}
                  >
                    <AnimatePresence mode="popLayout">
                      {filteredDealers.map((dealer) => (
                        <motion.div
                          key={dealer.id}
                          data-dealer-id={dealer.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          onClick={(e) => handleDealerClick(dealer, e)}
                          className={cn(
                            'mirror-card p-3 lg:p-4 space-y-2 lg:space-y-3 group cursor-pointer transition-all duration-300',
                            selectedDealerId === dealer.id ? 'border-primary bg-primary/5 shadow-elegant' : 'hover:border-primary/50'
                          )}
                        >
                          <div className="flex justify-between items-start gap-4">
                            <div className="space-y-0.5">
                              <div className="text-primary text-[8px] lg:text-[9px] uppercase tracking-widest font-bold opacity-80">
                                {getTypeLabel(dealer.type, language)}
                              </div>
                              <h3 className="text-base lg:text-lg font-bold uppercase tracking-tight leading-tight">{dealer.name}</h3>
                            </div>
                            <div className={cn(
                              'w-8 h-8 lg:w-9 lg:h-9 shrink-0 flex items-center justify-center transition-all',
                              selectedDealerId === dealer.id ? 'bg-primary text-white' : 'bg-white/5 text-white/30 group-hover:bg-primary group-hover:text-white'
                            )}>
                              <Navigation size={14} className="lg:size-[16px]" />
                            </div>
                          </div>

                          <div className="space-y-1.5 lg:space-y-2">
                            <div className="flex gap-2 items-start">
                              <MapPin size={13} className="text-primary shrink-0 mt-0.5" />
                              <div className="text-[11px] lg:text-xs text-white/50 leading-tight">
                                {dealer.city[language as 'ru' | 'en' | 'uz'] || dealer.city.ru}, {dealer.address[language as 'ru' | 'en' | 'uz'] || dealer.address.ru}
                              </div>
                            </div>
                            <div className="flex gap-2 items-center">
                              <Phone size={13} className="text-primary shrink-0" />
                              <div className="text-[11px] lg:text-xs text-white/50 leading-tight">{dealer.phone}</div>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDealerClick(dealer); trackClick('where-to-buy-view-on-map', '/where-to-buy'); }}
                            className="flex items-center gap-2 text-[8px] lg:text-[9px] font-bold uppercase tracking-widest text-primary hover:gap-3 transition-all duration-300 py-1"
                          >
                            {t('wtb.view_on_map')} <ExternalLink size={10} />
                          </button>
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {dealersLoaded && filteredDealers.length === 0 && (
                      <div className="py-12 lg:py-20 text-center space-y-4">
                        <p className="text-white/30 uppercase tracking-[0.2em] text-sm">{t('wtb.no_dealers')}</p>
                        <button onClick={() => setSearchQuery('')} className="text-primary font-bold text-xs uppercase tracking-widest min-h-[44px]">{t('wtb.show_all')}</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Map */}
                <div className="lg:col-span-2 order-1 lg:order-2 flex flex-col">
                  <div className="relative mirror-card overflow-hidden h-[360px] lg:h-[520px]">
                    <div
                      ref={mapContainerRef}
                      className={cn(
                        'absolute inset-0 w-full h-full z-10',
                        'yandex-map-grayscale transition-opacity duration-700',
                        mapStatus !== 'ready' && 'opacity-0'
                      )}
                      style={{ width: '100%', height: '100%' }}
                    />
                    {mapStatus === 'loading' && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 z-20">
                        <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
                        <p className="text-white/50 text-xs uppercase tracking-widest font-bold">
                          {t('wtb.map.loading') || 'Загрузка карты...'}
                        </p>
                      </div>
                    )}
                    {mapStatus === 'error' && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/90 z-20 p-8">
                        <AlertTriangle className="w-12 h-12 text-primary/60 mb-4" />
                        <p className="text-white/70 text-sm text-center mb-2 font-bold uppercase tracking-wider">
                          {t('wtb.map.unavailable') || 'Карта временно недоступна'}
                        </p>
                        <p className="text-white/40 text-xs text-center max-w-md">
                          {t('wtb.map.check_api_key') || 'Проверьте API-ключ Яндекс.Карт или попробуйте позже.'}
                        </p>
                      </div>
                    )}
                  </div>

                  {mapStatus === 'ready' && (
                    <div className="mt-4 lg:mt-6 bg-background/95 backdrop-blur-md p-6 lg:p-8 border border-primary/20 space-y-3 lg:space-y-4">
                      <div className="flex items-center gap-3 text-primary">
                        <Info size={18} />
                        <span className="text-[10px] lg:text-xs font-bold uppercase tracking-widest">{t('wtb.map.important_note')}</span>
                      </div>
                      <p className="text-white/60 text-[10px] lg:text-xs leading-relaxed">
                        {t('wtb.map.important_text')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};
